import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {Platform, Text, TouchableOpacity, View} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';

import {signOut} from '/redux/action/auth.action';
import {isSignedIn as isSignedInSelector} from '/redux/selector/currentUser.selector';

import {styles} from './WebSessionProvider.style';

interface WebSessionProviderProps {
  children: ReactNode;
}

// Production timeouts: 30 minutes total (25m idle + 5m warning)
const TOTAL_TIMEOUT_MS = 30 * 60 * 1000;
const WARNING_DURATION_MS = 5 * 60 * 1000;
const IDLE_BEFORE_WARNING_MS = TOTAL_TIMEOUT_MS - WARNING_DURATION_MS;

/**
 * WebSessionProvider handles:
 * 1. Initial session rehydration on web page refresh.
 * 2. Two-stage idle timeout (Warning -> Logout) for web security.
 */
export const WebSessionProvider = ({children}: WebSessionProviderProps) => {
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_DURATION_MS / 1000);
  const dispatch = useDispatch();
  const isSignedIn = useSelector(isSignedInSelector);

  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);

  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // --- 2. Idle Timeout Logic ---

  const clearAllTimers = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
  };

  const handleLogout = () => {
    setIsWarningVisible(false);
    clearAllTimers();
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    dispatch(signOut());
  };

  const startWarning = () => {
    if (isSignedIn) {
      setIsWarningVisible(true);
      setCountdown(WARNING_DURATION_MS / 1000);

      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = setTimeout(handleLogout, WARNING_DURATION_MS);

      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = setInterval(() => {
        setCountdown(prev => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
  };

  const startIdleTimer = () => {
    clearAllTimers();
    if (isSignedIn) {
      let elapsed = 0;
      heartbeatRef.current = setInterval(() => {
        elapsed += 5;
        if (elapsed < IDLE_BEFORE_WARNING_MS / 1000) {
        } else {
          if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        }
      }, 5000);

      idleTimerRef.current = setTimeout(startWarning, IDLE_BEFORE_WARNING_MS);
    }
  };

  const onStayLoggedIn = () => {
    setIsWarningVisible(false);
    startIdleTimer();
  };

  useEffect(() => {
    if (Platform.OS !== 'web' || !isSignedIn) {
      clearAllTimers();
      setIsWarningVisible(false);
      return;
    }

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
    ];

    startIdleTimer();

    const activityHandler = () => {
      // Only reset if warning is NOT showing.
      setIsWarningVisible(visible => {
        if (!visible) {
          startIdleTimer();
        }
        return visible;
      });
    };

    events.forEach(event => window.addEventListener(event, activityHandler));

    return () => {
      clearAllTimers();
      events.forEach(event =>
        window.removeEventListener(event, activityHandler),
      );
    };
  }, [isSignedIn, dispatch]);

  return (
    <>
      {children}
      {isWarningVisible && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Inactivity Warning</Text>
            <Text style={styles.modalDescription}>
              Your session will expire in{' '}
              <Text style={styles.countdownText}>
                {formatCountdown(countdown)}
              </Text>{' '}
              due to inactivity. Click the button below to stay logged in.
            </Text>
            <TouchableOpacity
              style={styles.modalButton}
              onPress={onStayLoggedIn}
              activeOpacity={0.7}>
              <Text style={styles.modalButtonText}>Stay Logged In</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.logoutLink}
              onPress={handleLogout}
              activeOpacity={0.7}>
              <Text style={styles.logoutLinkText}>Logout Now</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </>
  );
};

export default WebSessionProvider;
